import styled from "@emotion/styled";
import { useProfileContext } from "@hooks/context/useProfileContext";
import { useProjectContext } from "@hooks/context/useProjectContext";
import { useSidebarContext } from "@hooks/context/useSidebarContext";
import { Skeleton } from "@mui/material";
import { TOP_BAR_HEIGHT } from "@themes/tokens/sidebar";
import { TOP_BAR_SEARCH_WIDTH } from "@ui/organisms/Header/Header.components";
import { useTopBarSlot } from "@ui/organisms/Header/TopBarSlot";

import {
  AppTopBarTrail,
  CrumbScope,
  CrumbSection,
  CrumbSeparator
} from "./AppTopBarCrumbs";
import { AppTopBarUtilityActions } from "./AppTopBarUtilityActions";

/** From the design: the content's 26px side padding. Exported because the
 * page below has to start on the same line as the crumbs. */
export const TOP_BAR_PADDING_X = 26;

const Bar = styled("header")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.vars.ui.topBar.background,
  borderBottom: `1px solid ${theme.vars.ui.topBar.line}`,
  boxSizing: "border-box",
  display: "flex",
  flex: "none",
  gap: 16,
  height: TOP_BAR_HEIGHT,
  justifyContent: "space-between",
  padding: `0 ${TOP_BAR_PADDING_X}px`,
  // Stays put while the page scrolls beneath it. `main` grows with its
  // content (see MainPageLayout), so the sticky range is the whole page.
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar
}));

const Crumbs = styled("nav")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  // Shrinkable, and the first thing to give: the controls on the other side
  // are all fixed-size, so if the crumbs will not yield the two collide. They
  // did, at every width under about 900px.
  flex: "0 1 auto",
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  gap: 10,
  letterSpacing: "0.08em",
  lineHeight: 1,
  minWidth: 0,
  overflow: "hidden",
  textTransform: "uppercase"
}));

/**
 * Where a page's `Header` puts its actions when placed in the bar.
 *
 * Bar density lives here rather than on each atom: every control in the app
 * is built for a page header, where 40px is right. In a 57px bar that leaves
 * no air, so the cluster is scaled down as a whole — buttons 32px tall, and
 * the tools inside the group 28px so the group is 32 including its padding.
 * Everywhere else those atoms are untouched.
 */
/*
 * Two of the rules below concern the split button (`MuiButtonGroup`) that the
 * Smart Directory's sort control is. The keys are sorted, so the note lives
 * here rather than beside them:
 *
 * - The collapse-to-a-glyph rule is for the animated action buttons, which
 *   carry `data-expanded`. A split button's halves carry nothing of the
 *   kind, so without `:not(.MuiButtonGroup-grouped)` the sort control was
 *   squeezed to 32px at rest with its label spilling out over the chevron,
 *   and only grew to fit on hover.
 * - A split button in the bar is a bar control like the rest: 32px tall,
 *   flat, on a hairline — not the page header's raised card. That is the
 *   `.MuiButtonGroup-root` rules.
 */
const Actions = styled("div")(({ theme }) => ({
  // 20px glyphs are sized for a 40px page-header control; in a 32px one they
  // crowd it. Doubled: `Icon` sets its size on the element as well as in a
  // class, and a utility button's glyph was staying at 20 beside 16s.
  "&& svg": {
    height: theme.controls.iconSize,
    width: theme.controls.iconSize
  },
  "& .animated-text": {
    marginLeft: theme.controls.iconGap
  },
  "& .MuiButton-root, & .MuiTypography-button": {
    fontSize: theme.typography.body2.fontSize
  },
  '& .MuiButton-root:not([data-expanded="true"]):not(:hover) .animated-text': {
    marginLeft: 0,
    width: 0
  },
  '& .MuiButton-root:not([data-expanded="true"]):not(:hover):not(.MuiButtonGroup-grouped)':
    {
      minWidth: theme.controls.height,
      paddingLeft: (theme.controls.height - theme.controls.iconSize) / 2 - 1,
      paddingRight: (theme.controls.height - theme.controls.iconSize) / 2 - 1,
      width: theme.controls.height
    },
  '& .MuiButton-root[data-expanded="true"]': {
    width: "max-content"
  },
  // Toggle segments keep the theme's segment height inside their 32px track.
  "& .MuiButtonBase-root:not(.MuiToggleButton-root)": {
    height: theme.controls.height,
    minHeight: theme.controls.height,
    paddingBottom: 0,
    paddingTop: 0
  },
  "& .MuiButtonGroup-root": {
    background: "none",
    border: `1px solid ${theme.vars.ui.topBar.line}`,
    borderRadius: theme.shape.borderRadiusButton,
    boxShadow: "none",
    height: theme.controls.height,
    minHeight: theme.controls.height
  },
  "& .MuiButtonGroup-root .MuiButtonGroup-grouped": {
    height: theme.controls.height - 2,
    minHeight: theme.controls.height - 2,
    paddingLeft: 12,
    paddingRight: 12
  },
  "& .MuiButtonGroup-root .MuiButtonGroup-grouped:last-of-type": {
    minWidth: theme.controls.height - 2,
    paddingLeft: 4,
    paddingRight: 4
  },
  '& .MuiSkeleton-root[data-expanded="false"]': {
    minWidth: theme.controls.height,
    width: theme.controls.height
  },
  "& .MuiSkeleton-root[data-expanded]": {
    height: theme.controls.height
  },
  // Each control draws its own flat, hairline-bordered box.
  '& [data-in-top-bar="true"] .MuiButtonBase-root': {
    background: "none",
    border: `1px solid ${theme.vars.ui.topBar.line}`,
    borderRadius: theme.shape.borderRadiusButton,
    boxShadow: "none",
    boxSizing: "border-box",
    height: theme.controls.height,
    minHeight: theme.controls.height,
    minWidth: theme.controls.height
  },
  // Hover is the bar's own: MUI paints `action.hover` here, which is a light
  // grey in dark mode and lit these controls up like they were pressed.
  '& [data-in-top-bar="true"] .MuiButtonBase-root:hover, & [data-in-top-bar="true"] .MuiFormControl-root:hover':
    {
      backgroundColor: theme.vars.ui.topBar.hover,
      borderColor: theme.vars.ui.topBar.lineHover
    },
  '& [data-in-top-bar="true"] .MuiFormControl-root': {
    border: `1px solid ${theme.vars.ui.topBar.line}`,
    borderRadius: theme.shape.borderRadiusButton,
    boxSizing: "border-box",
    height: theme.controls.height
  },
  '& [data-in-top-bar="true"] .MuiInputBase-input': {
    padding: 0
  },
  '& [data-in-top-bar="true"] .MuiInputBase-root': {
    paddingBottom: 0,
    paddingTop: 0
  },
  '& [data-in-top-bar="true"] .MuiSkeleton-root': {
    borderRadius: theme.shape.borderRadiusButton
  },
  // Shut, the search is a square like the tools beside it; clicking it opens
  // it to the design's width. The widths are pinned here rather than left to
  // `SearchBox` because its own collapsed size is 48px — right for a page
  // header, a size too wide for a row of 32px controls — and because below
  // `sm` it goes full-width, which in a bar means over everything else.
  '& [data-in-top-bar="true"] .search-container': {
    overflow: "hidden",
    width: theme.controls.height
  },
  // Shut, the box is a square holding one glyph: the field's own 14px left
  // padding and the adornment's margin are what put the magnifier 7px right
  // of centre while every tool beside it was dead centre.
  '& [data-in-top-bar="true"] .search-container:not([data-expanded="true"]) .MuiInputAdornment-root':
    {
      margin: 0
    },
  // …and the input itself is folded away, or its placeholder peeks out beside
  // the glyph and pushes it off centre the other way.
  '& [data-in-top-bar="true"] .search-container:not([data-expanded="true"]) .MuiInputBase-input':
    {
      minWidth: 0,
      opacity: 0,
      padding: 0,
      width: 0
    },
  '& [data-in-top-bar="true"] .search-container:not([data-expanded="true"]) .MuiInputBase-root':
    {
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0
    },
  '& [data-in-top-bar="true"] .search-container[data-expanded="true"]': {
    maxWidth: "100%",
    width: TOP_BAR_SEARCH_WIDTH
  },
  alignItems: "center",
  display: "flex",
  flex: "none",
  gap: 6,
  // Empty until a page portals into it; must not collapse the bar's layout.
  minHeight: 1,
  minWidth: 0
}));

/**
 * Everything on the right of the crumbs: the page's portalled actions, then
 * the divider, then the app's own pair. Fixed-size as a whole, so the crumbs
 * stay the one thing that shrinks.
 */
const Right = styled("div")({
  alignItems: "center",
  display: "flex",
  flex: "none",
  gap: 6,
  minWidth: 0
});

/**
 * The strip across the top of the content: `SCOPE / SECTION` on the left, and
 * on the right whatever the page's `Header` chooses to put there.
 *
 * The scope is whichever the nav is showing — the project when a project is
 * selected, else the organisation — and the section is the nav group the page
 * sits in, published by the sidebar wrapper. Neither is page-owned: the bar
 * is the one part of the page that reads the same from every route, which is
 * what makes it a bar and not another header.
 */
export const AppTopBar = () => {
  const { isAuthProfileLoading, userOrganisation } = useProfileContext();
  const { selectedProject } = useProjectContext();
  const { sectionLabel } = useSidebarContext();
  const { setSlot, trail } = useTopBarSlot();

  const scopeLabel =
    selectedProject?.projectName ?? userOrganisation?.displayName ?? "";

  return (
    <Bar data-test-id="app-top-bar">
      <Crumbs aria-label="Breadcrumb">
        {isAuthProfileLoading && !scopeLabel ? (
          <Skeleton variant="text" width={120} height={12} />
        ) : (
          <CrumbScope title={scopeLabel}>{scopeLabel}</CrumbScope>
        )}
        {trail?.length ? (
          <AppTopBarTrail trail={trail} />
        ) : sectionLabel ? (
          <>
            <CrumbSeparator aria-hidden>/</CrumbSeparator>
            <CrumbSection>{sectionLabel}</CrumbSection>
          </>
        ) : null}
      </Crumbs>
      <Right>
        {/* A callback ref, not useRef + effect: the slot must be registered
            the moment the element exists, or a page's first render finds no
            slot and draws its actions inline for one frame before jumping
            into the bar. `setSlot` is the state setter, so it is stable. */}
        <Actions ref={setSlot} data-test-id="app-top-bar-actions" />
        <AppTopBarUtilityActions />
      </Right>
    </Bar>
  );
};
